import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Button,
  useTheme,
  Container,
  Tooltip,
  ListItemButton,
} from "@mui/material";

import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  HowToReg,
  DoneAll,
  ManageAccounts,
  Storefront,
  Assessment,
} from "@mui/icons-material";

import { Link, useNavigate, useLocation } from "react-router-dom";
import AppLogo from "./../resources/AppSVGLogo.png";
import { useAuth } from "../auth/AuthContext";

function Navbar() {
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();

  const {
    currentUser,
    isLoggedIn,
    isSuperAdmin,
    isAppAdmin,
    isAppUser,
    logout,
  } = useAuth();

  const userEmail = currentUser?.email || "";

  const toggleDrawer = (open) => setOpenDrawer(open);

  const handleOpenUserMenu = (e) => setAnchorElUser(e.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  const handleLoginLogout = async () => {
    if (isLoggedIn) {
      try {
        await logout();
      } catch (err) {
        console.error("Logout error:", err);
      }
      navigate("/login", { replace: true });
    } else {
      navigate("/login");
    }
    handleCloseUserMenu();
  };

  const getInitials = (email) => {
    if (!email) return "?";
    const name = email.split("@")[0];
    const parts = name.split(".");
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0][0]?.toUpperCase() || "?";
  };

  // ── Drawer content ───────────────────────────────────────────────────
  // Visibility rules per role:
  //   SuperAdmin: only Tenant Management
  //   AppAdmin:   User Management + Admin Checklist + User Checklist + CBAM
  //   AppUser:    User Checklist + CBAM
  //   Verifier:   nothing (for now)
  const showTenantMgmt = isSuperAdmin;
  const showUserMgmt = isAppAdmin && !isSuperAdmin;
  const showAdminChecklist = isSuperAdmin || isAppAdmin;
  const showUserChecklist = isSuperAdmin || isAppAdmin || isAppUser;
  const showCbam = isSuperAdmin || isAppAdmin || isAppUser;
  const showAdminSection = showTenantMgmt || showUserMgmt;
  const showAuditSection = showAdminChecklist || showUserChecklist;
  const showCbamSection = showCbam;

  const DrawerList = (
    <Box
      sx={{
        width: 280,
        backgroundColor: "background.paper",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box sx={{ flex: 1, overflowY: "auto", p: 0 }}>
        {/* ADMINISTRATION SECTION */}
        {showAdminSection && (
          <Box>
            <Typography
              variant="overline"
              sx={{
                display: "block",
                px: 2,
                pt: 2,
                pb: 1,
                fontWeight: 700,
                color: "#666",
                fontSize: "0.75rem",
                letterSpacing: "0.5px",
              }}
            >
              ADMINISTRATION
            </Typography>

            <List sx={{ py: 0 }}>
              {showTenantMgmt && (
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    component={Link}
                    to="/tenant-management"
                    onClick={() => toggleDrawer(false)}
                    selected={location.pathname === "/tenant-management"}
                    sx={{
                      pl: 2,
                      pr: 2,
                      py: 1,
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Storefront fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Tenant Management"
                      primaryTypographyProps={{ fontSize: "0.95rem" }}
                    />
                  </ListItemButton>
                </ListItem>
              )}

              {showUserMgmt && (
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    component={Link}
                    to="/user-management"
                    onClick={() => toggleDrawer(false)}
                    selected={location.pathname === "/user-management"}
                    sx={{
                      pl: 2,
                      pr: 2,
                      py: 1,
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <ManageAccounts fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="User Management"
                      primaryTypographyProps={{ fontSize: "0.95rem" }}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </List>

            {!isSuperAdmin && (showAuditSection || showCbamSection) && (
              <Divider sx={{ my: 1 }} />
            )}
          </Box>
        )}

        {/* AUDITS SECTION */}
        {!isSuperAdmin && showAuditSection && (
          <Box>
            <Typography
              variant="overline"
              sx={{
                display: "block",
                px: 2,
                pt: 2,
                pb: 1,
                fontWeight: 700,
                color: "#666",
                fontSize: "0.75rem",
                letterSpacing: "0.5px",
              }}
            >
              ENERGY AUDIT
            </Typography>

            <List sx={{ py: 0 }}>
              {showAdminChecklist && (
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    component={Link}
                    to="/admin"
                    onClick={() => toggleDrawer(false)}
                    selected={location.pathname === "/admin"}
                    sx={{
                      pl: 2,
                      pr: 2,
                      py: 1,
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <HowToReg fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Create Custom Report"
                      primaryTypographyProps={{ fontSize: "0.95rem" }}
                    />
                  </ListItemButton>
                </ListItem>
              )}

              {showUserChecklist && (
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    component={Link}
                    to="/user"
                    onClick={() => toggleDrawer(false)}
                    selected={location.pathname === "/user"}
                    sx={{
                      pl: 2,
                      pr: 2,
                      py: 1,
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <DoneAll fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Update Assigned Report"
                      primaryTypographyProps={{ fontSize: "0.95rem" }}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </List>

            {showCbamSection && <Divider sx={{ my: 1 }} />}
          </Box>
        )}

        {/* CBAM SECTION */}
        {!isSuperAdmin && showCbamSection && (
          <Box>
            <Typography
              variant="overline"
              sx={{
                display: "block",
                px: 2,
                pt: 2,
                pb: 1,
                fontWeight: 700,
                color: "#666",
                fontSize: "0.75rem",
                letterSpacing: "0.5px",
              }}
            >
              CBAM
            </Typography>

            <List sx={{ py: 0 }}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to="/audits"
                  onClick={() => toggleDrawer(false)}
                  selected={
                    location.pathname === "/audits" ||
                    location.pathname.startsWith("/form-") ||
                    location.pathname.startsWith("/summary")
                  }
                  sx={{
                    pl: 2,
                    pr: 2,
                    py: 1,
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Assessment fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="CBAM Reports"
                    primaryTypographyProps={{ fontSize: "0.95rem" }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
            <Divider sx={{ my: 1 }} />

            <List sx={{ py: 0 }}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={handleLoginLogout}
                  sx={{
                    pl: 2,
                    pr: 2,
                    py: 1,
                    "&:hover": {
                      backgroundColor: "rgba(244,67,54,0.08)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Logout fontSize="small" color="error" />
                  </ListItemIcon>

                  <ListItemText
                    primary="Logout"
                    primaryTypographyProps={{
                      fontSize: "0.95rem",
                      color: "error.main",
                      fontWeight: 500,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar position="fixed" elevation={2}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: "48px !important", px: 2 }}>
            {isLoggedIn && (
              <IconButton
                onClick={() => toggleDrawer(true)}
                color="inherit"
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Link
              to="/home"
              style={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
              }}
            >
              <img
                src={AppLogo}
                alt="logo"
                style={{ width: 32, height: 32, marginRight: 10 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 600, color: "white" }}>
                AUDIT APP
              </Typography>
            </Link>

            <Box sx={{ flexGrow: 1 }} />

            {isLoggedIn ? (
              <>
                <Tooltip title="User Menu">
                  <IconButton onClick={handleOpenUserMenu}>
                    <Avatar
                      sx={{
                        width: 42,
                        height: 42,
                        fontSize: "1rem",
                        fontWeight: 700,
                        color: "#fff",

                        background:
                          "linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)",

                        border: "2px solid rgba(255,255,255,0.9)",

                        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",

                        transition: "all 0.25s ease",

                        "&:hover": {
                          transform: "scale(1.08)",
                          boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
                        },
                      }}
                    >
                      {getInitials(userEmail)}
                    </Avatar>
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Button component={Link} to="/login" color="inherit">
                Login
              </Button>
            )}

            <Menu
              anchorEl={anchorElUser}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              sx={{ mt: "45px" }}
            >
              <MenuItem onClick={handleLoginLogout}>
                <ListItemIcon>
                  {isLoggedIn ? (
                    <Logout fontSize="small" />
                  ) : (
                    <AccountCircle fontSize="small" />
                  )}
                </ListItemIcon>
                <Typography>{isLoggedIn ? "Logout" : "Login"}</Typography>
              </MenuItem>
            </Menu>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="left"
        open={openDrawer}
        onClose={() => toggleDrawer(false)}
        PaperProps={{
          sx: {
            top: "48px",
            height: "calc(100% - 48px)",
            borderRight: "1px solid #ddd",
            maxHeight: "calc(100vh - 48px)",
            overflowY: "auto",
          },
        }}
      >
        {DrawerList}
      </Drawer>
    </>
  );
}

export default Navbar;
